/**
 * Int8 vector quantization.
 *
 * Pure and isomorphic: runs identically in the browser and in Node, so the
 * exporter and the loader cannot drift apart. A pack quantized by the tool is
 * dequantized by the page using this same code.
 *
 * 4217 facts at 768 dims is 3.2 MB as int8 against 13 MB as float32. That
 * difference is the whole reason a lattice can load in about a second while
 * the model is still downloading.
 */

/** L2-normalize in place. Cosine similarity then reduces to a dot product. */
export function normalize(v: Float32Array): Float32Array {
  let sum = 0;
  for (let i = 0; i < v.length; i++) sum += v[i] * v[i];
  const n = Math.sqrt(sum);
  if (n > 1e-12) for (let i = 0; i < v.length; i++) v[i] /= n;
  return v;
}

export type QuantizedVectors = {
  dim: number;
  count: number;
  /** count * dim int8 values, row-major. */
  data: Int8Array;
  /** One scale per row: value = data[i] * scale[row]. */
  scales: Float32Array;
};

/**
 * Quantize row-major float vectors. Each row gets its own scale, which costs
 * 4 bytes per vector and buys real precision on rows whose largest component
 * is small.
 */
export function quantize(vectors: Float32Array, count: number, dim: number): QuantizedVectors {
  if (vectors.length !== count * dim) {
    throw new Error(`quantize: expected ${count * dim} floats, got ${vectors.length}`);
  }
  const data = new Int8Array(count * dim);
  const scales = new Float32Array(count);

  for (let r = 0; r < count; r++) {
    const off = r * dim;
    let max = 0;
    for (let i = 0; i < dim; i++) {
      const a = Math.abs(vectors[off + i]);
      if (a > max) max = a;
    }
    // An all-zero row is degenerate but must not produce NaN.
    const scale = max > 0 ? max / 127 : 1;
    scales[r] = scale;
    const inv = 1 / scale;
    for (let i = 0; i < dim; i++) {
      // Clamp defensively: rounding can reach 128 at the extreme.
      const q = Math.round(vectors[off + i] * inv);
      data[off + i] = q > 127 ? 127 : q < -128 ? -128 : q;
    }
  }
  return { dim, count, data, scales };
}

/** Dequantize one row into a caller-supplied buffer, avoiding allocation. */
export function dequantizeRow(q: QuantizedVectors, row: number, out: Float32Array): Float32Array {
  const off = row * q.dim;
  const s = q.scales[row];
  for (let i = 0; i < q.dim; i++) out[i] = q.data[off + i] * s;
  return out;
}

/**
 * Mean absolute error between the original and its round trip. Used by the
 * bench to prove int8 is not costing recall, rather than assuming it.
 */
export function quantizationError(original: Float32Array, q: QuantizedVectors): number {
  const buf = new Float32Array(q.dim);
  let sum = 0;
  for (let r = 0; r < q.count; r++) {
    dequantizeRow(q, r, buf);
    const off = r * q.dim;
    for (let i = 0; i < q.dim; i++) sum += Math.abs(original[off + i] - buf[i]);
  }
  return sum / (q.count * q.dim);
}
